/**
 * DisplayDrawer component
 *
 * Bottom drawer for non-listing display controls, grouped by what they
 * affect:
 * - Map: Satellite view, My Location
 * - Overlay: Off / Zoning / Trends / Climate - a single exclusive choice.
 *   Zoning only ever fills neighborhoods (no district-level equivalent).
 *   Trends and Climate are neighborhood-only too for now - district fill
 *   support still exists in Map.ts (setDistrictFillMode) but isn't wired
 *   up from here, since districts span too much ground (mountain to sea in
 *   some cases) for one fill color to mean anything. Overlay is also where
 *   future non-price, non-zoning data (flooding, etc.) belongs - grouped
 *   under this row rather than each getting its own top-level row, which
 *   would just move the "too many buttons" problem here.
 * - Neighborhood: Labels (names + click-to-enter-NHM) / Outline (plain
 *   border).
 * - District: Labels (names) / Outline (plain border) - no fill option.
 *
 * Handle opens with drawer and clicking it closes the drawer.
 */
import { injectStyles } from './styles.js';
const OVERLAY_TOGGLES = [
    { value: 'none', label: 'Off' },
    { value: 'zoning', label: 'Zoning' },
    { value: 'trends', label: 'Trends' },
    { value: 'climate', label: 'Climate' },
];
export class DisplayDrawer {
    container;
    options;
    onDisplayChange;
    onMyLocation;
    isOpen = false;
    constructor(opts) {
        injectStyles();
        this.container = opts.container;
        this.onDisplayChange = opts.onDisplayChange;
        this.onMyLocation = opts.onMyLocation;
        this.options = {
            satellite: opts.initialOptions?.satellite ?? false,
            overlay: opts.initialOptions?.overlay ?? 'none',
            neighborhoods: {
                labels: opts.initialOptions?.neighborhoods?.labels ?? true,
                outline: opts.initialOptions?.neighborhoods?.outline ?? false,
            },
            districts: {
                labels: opts.initialOptions?.districts?.labels ?? false,
                outline: opts.initialOptions?.districts?.outline ?? false,
            },
        };
        this.render();
        this.attachEventListeners();
    }
    renderOverlayToggles() {
        return OVERLAY_TOGGLES.map((t) => `
      <button type="button" class="display-toggle${this.options.overlay === t.value ? ' active' : ''}" data-overlay="${t.value}">${t.label}</button>
    `).join('');
    }
    render() {
        this.container.innerHTML = `
      <div class="display-wrapper" id="display-drawer-component">
        <div class="display-panel" id="display-drawer-panel">
          <div class="display-panel-body">
            <div class="display-group">
              <div class="display-group-title">Map</div>
              <div class="display-group-controls">
                <label class="display-option">
                  <input type="checkbox" id="display-satellite" ${this.options.satellite ? 'checked' : ''}>
                  <span class="display-option-label">Satellite</span>
                </label>
                <button class="display-location-btn" id="display-my-location">
                  <svg class="display-location-icon" width="12" height="12" viewBox="0 0 16 16"><circle cx="8" cy="8" r="6" fill="#4285F4" stroke="#fff" stroke-width="2"/></svg>
                  My Location
                </button>
              </div>
            </div>

            <div class="display-group">
              <div class="display-group-title">Overlay</div>
              <div class="display-group-controls">
                <div class="display-toggle-group" data-toggle-group="overlay">
                  ${this.renderOverlayToggles()}
                </div>
              </div>
            </div>

            <div class="display-group">
              <div class="display-group-title">Neighborhood</div>
              <div class="display-group-controls">
                <label class="display-option">
                  <input type="checkbox" id="display-neighborhood-labels" ${this.options.neighborhoods.labels ? 'checked' : ''}>
                  <span class="display-option-label">Labels</span>
                </label>
                <label class="display-option">
                  <input type="checkbox" id="display-neighborhood-outline" ${this.options.neighborhoods.outline ? 'checked' : ''}>
                  <span class="display-option-label">Outline</span>
                </label>
              </div>
            </div>

            <div class="display-group">
              <div class="display-group-title">District</div>
              <div class="display-group-controls">
                <label class="display-option">
                  <input type="checkbox" id="display-district-labels" ${this.options.districts.labels ? 'checked' : ''}>
                  <span class="display-option-label">Labels</span>
                </label>
                <label class="display-option">
                  <input type="checkbox" id="display-district-outline" ${this.options.districts.outline ? 'checked' : ''}>
                  <span class="display-option-label">Outline</span>
                </label>
              </div>
            </div>
          </div>
        </div>
        <div class="display-handle" id="display-drawer-handle" aria-expanded="${this.isOpen ? 'true' : 'false'}">
          <svg class="display-handle-arrow" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#000" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><path d="M6 9l6 6 6-6"/></svg>
          <span class="display-handle-label">Display</span>
          <div class="display-handle-icons" id="display-handle-icons">
            ${this.renderHandleIcons()}
          </div>
        </div>
      </div>
    `;
    }
    // Which fill (if any) is currently active, for the handle's at-a-glance
    // badge - a short text label rather than a growing set of pictograms.
    // Zoning and Overlay are mutually exclusive, so at most one of these is
    // ever true at once.
    activeFillLabel() {
        if (this.options.overlay === 'zoning')
            return 'Zoning';
        if (this.options.overlay === 'trends')
            return 'Trends';
        if (this.options.overlay === 'climate')
            return 'Climate';
        return null;
    }
    renderHandleIcons() {
        const icons = [];
        if (this.options.satellite) {
            icons.push('<span class="display-icon active">🌍</span>');
        }
        if (this.options.neighborhoods.labels) {
            icons.push('<span class="display-icon active">🏘️</span>');
        }
        if (this.options.districts.labels) {
            icons.push('<span class="display-icon active">🗺️</span>');
        }
        if (this.options.neighborhoods.outline || this.options.districts.outline) {
            icons.push('<span class="display-icon active">▭</span>');
        }
        const fillLabel = this.activeFillLabel();
        if (fillLabel) {
            icons.push(`<span class="display-icon-badge">${fillLabel}</span>`);
        }
        if (icons.length === 0) {
            icons.push('<span class="display-icon">⚙</span>');
        }
        return icons.join('');
    }
    updateHandleIcons() {
        const container = this.container.querySelector('#display-handle-icons');
        if (container) {
            container.innerHTML = this.renderHandleIcons();
        }
    }
    updateOverlayToggleButtons() {
        this.container.querySelectorAll('[data-overlay]').forEach((el) => {
            const btn = el;
            btn.classList.toggle('active', this.options.overlay === btn.dataset.overlay);
        });
    }
    /**
     * Overlay buttons are a single exclusive choice (Off/Zoning/Trends/
     * Climate) - an explicit Off button rather than requiring a
     * click-the-active-one-again gesture, which wasn't discoverable.
     */
    selectOverlay(mode) {
        this.options.overlay = mode;
        this.updateOverlayToggleButtons();
        this.emitChange();
    }
    attachEventListeners() {
        const handle = this.container.querySelector('#display-drawer-handle');
        // Handle click toggles drawer
        handle?.addEventListener('click', (e) => {
            e.stopPropagation();
            if (this.isOpen) {
                this.close();
            }
            else {
                this.open();
            }
        });
        // Escape key to close
        document.addEventListener('keydown', (e) => {
            if (e.key === 'Escape' && this.isOpen) {
                this.close();
            }
        });
        // Satellite toggle
        const satellite = this.container.querySelector('#display-satellite');
        satellite?.addEventListener('change', () => {
            this.options.satellite = satellite.checked;
            this.emitChange();
        });
        // Neighborhood labels on/off
        const nLabels = this.container.querySelector('#display-neighborhood-labels');
        nLabels?.addEventListener('change', () => {
            this.options.neighborhoods.labels = nLabels.checked;
            this.emitChange();
        });
        // Neighborhood outline on/off
        const nOutline = this.container.querySelector('#display-neighborhood-outline');
        nOutline?.addEventListener('change', () => {
            this.options.neighborhoods.outline = nOutline.checked;
            this.emitChange();
        });
        // District labels on/off
        const dLabels = this.container.querySelector('#display-district-labels');
        dLabels?.addEventListener('change', () => {
            this.options.districts.labels = dLabels.checked;
            this.emitChange();
        });
        // District outline on/off
        const dOutline = this.container.querySelector('#display-district-outline');
        dOutline?.addEventListener('change', () => {
            this.options.districts.outline = dOutline.checked;
            this.emitChange();
        });
        // Overlay toggle buttons (Off / Zoning / Trends / Climate)
        this.container.querySelectorAll('[data-overlay]').forEach((el) => {
            el.addEventListener('click', () => {
                const mode = el.dataset.overlay;
                this.selectOverlay(mode);
            });
        });
        // My Location button
        const myLocation = this.container.querySelector('#display-my-location');
        myLocation?.addEventListener('click', () => {
            if (this.onMyLocation) {
                this.onMyLocation();
            }
        });
    }
    emitChange() {
        this.updateHandleIcons();
        if (this.onDisplayChange) {
            this.onDisplayChange({ ...this.options, neighborhoods: { ...this.options.neighborhoods }, districts: { ...this.options.districts } });
        }
    }
    open() {
        const drawer = this.container.querySelector('#display-drawer-component');
        drawer?.classList.add('open');
        this.container.querySelector('#display-drawer-handle')?.setAttribute('aria-expanded', 'true');
        this.isOpen = true;
    }
    close() {
        const drawer = this.container.querySelector('#display-drawer-component');
        drawer?.classList.remove('open');
        this.container.querySelector('#display-drawer-handle')?.setAttribute('aria-expanded', 'false');
        this.isOpen = false;
    }
    /** Visual on/off state for the My Location button - tracking is a toggle
     * (watchPosition start/stop), so the button needs to reflect which state
     * it's in, matching Google Maps' own highlighted-when-active affordance. */
    setMyLocationActive(active) {
        const btn = this.container.querySelector('#display-my-location');
        btn?.classList.toggle('active', active);
    }
    getOptions() {
        return { ...this.options, neighborhoods: { ...this.options.neighborhoods }, districts: { ...this.options.districts } };
    }
    setOptions(options) {
        if (options.satellite !== undefined) {
            this.options.satellite = options.satellite;
            const checkbox = this.container.querySelector('#display-satellite');
            if (checkbox)
                checkbox.checked = options.satellite;
        }
        if (options.overlay !== undefined) {
            this.options.overlay = options.overlay;
        }
        if (options.neighborhoods?.labels !== undefined) {
            this.options.neighborhoods.labels = options.neighborhoods.labels;
            const checkbox = this.container.querySelector('#display-neighborhood-labels');
            if (checkbox)
                checkbox.checked = options.neighborhoods.labels;
        }
        if (options.neighborhoods?.outline !== undefined) {
            this.options.neighborhoods.outline = options.neighborhoods.outline;
            const checkbox = this.container.querySelector('#display-neighborhood-outline');
            if (checkbox)
                checkbox.checked = options.neighborhoods.outline;
        }
        if (options.districts?.labels !== undefined) {
            this.options.districts.labels = options.districts.labels;
            const checkbox = this.container.querySelector('#display-district-labels');
            if (checkbox)
                checkbox.checked = options.districts.labels;
        }
        if (options.districts?.outline !== undefined) {
            this.options.districts.outline = options.districts.outline;
            const checkbox = this.container.querySelector('#display-district-outline');
            if (checkbox)
                checkbox.checked = options.districts.outline;
        }
        this.updateOverlayToggleButtons();
        this.updateHandleIcons();
    }
}
//# sourceMappingURL=DisplayDrawer.js.map